import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Header from '../../components/Header'
import { useTheme } from '../../contexts/ThemeContext'
import { useAuth } from '../../contexts/AuthContext'
import { reportsAPI, appealsAPI, getApiBaseUrlWithoutApi } from '../../config/api'
import ToastNotification from '../../components/ToastNotification'
import 'primeicons/primeicons.css'

export default function ReportsAppealsPage() {
  const { theme } = useTheme()
  const { isAuthenticated, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [reports, setReports] = useState<any[]>([])
  const [appeals, setAppeals] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReport, setSelectedReport] = useState<any>(null)
  const [showAppealForm, setShowAppealForm] = useState(false)
  const [appealReason, setAppealReason] = useState('')
  const [appealEvidence, setAppealEvidence] = useState<File | null>(null)
  const [submittingAppeal, setSubmittingAppeal] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login')
    }
  }, [isAuthenticated, authLoading, navigate])

  useEffect(() => {
    if (isAuthenticated) {
      fetchData()
    }
  }, [isAuthenticated])

  const fetchData = async () => {
    try {
      setLoading(true)
      const [reportsData, appealsData] = await Promise.all([
        reportsAPI.getMyReportsReceived().catch(() => []),
        appealsAPI.getAppeals().catch(() => [])
      ])
      // Đảm bảo luôn là array
      setReports(Array.isArray(reportsData) ? reportsData : [])
      setAppeals(Array.isArray(appealsData) ? appealsData : [])
    } catch (error) {
      setToast({ message: 'Không thể tải dữ liệu', type: 'error' })
      setReports([])
      setAppeals([])
    } finally {
      setLoading(false)
    }
  }

  const handleAppealSubmit = async () => {
    if (!selectedReport) return
    
    if (!appealReason.trim()) {
      setToast({ message: 'Vui lòng nhập lý do kháng cáo', type: 'error' })
      return
    }

    try {
      setSubmittingAppeal(true)
      await appealsAPI.createAppeal({
        report: selectedReport.id,
        reason: appealReason,
        evidence_image: appealEvidence || undefined
      })
      
      setToast({ message: 'Đã gửi kháng cáo thành công. Vui lòng chờ admin duyệt.', type: 'success' })
      setShowAppealForm(false)
      setSelectedReport(null)
      setAppealReason('')
      setAppealEvidence(null)
      fetchData()
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 
                           error.response?.data?.report?.[0] || 
                           error.response?.data?.non_field_errors?.[0] ||
                           'Không thể gửi kháng cáo'
      
      // Kiểm tra nếu lỗi liên quan đến migration
      if (errorMessage.includes('appeals') || errorMessage.includes('migration') || errorMessage.includes('chưa được tạo')) {
        setToast({ 
          message: 'Hệ thống đang được cập nhật. Vui lòng thử lại sau.', 
          type: 'error' 
        })
      } else {
        setToast({ 
          message: errorMessage, 
          type: 'error' 
        })
      }
    } finally {
      setSubmittingAppeal(false)
    }
  }

  const getReportTypeDisplay = (type: string) => {
    const types: { [key: string]: string } = {
      'cheating': 'Gian lận điểm danh',
      'inappropriate_content': 'Nội dung không phù hợp',
      'harassment': 'Quấy rối',
      'other': 'Khác'
    }
    return types[type] || type
  }

  const getStatusDisplay = (status: string) => {
    const statuses: { [key: string]: string } = {
      'pending': 'Chờ xử lý',
      'investigating': 'Đang điều tra',
      'resolved': 'Đã xử lý',
      'dismissed': 'Đã bỏ qua'
    }
    return statuses[status] || status
  }

  const getAppealStatusDisplay = (status: string) => {
    const statuses: { [key: string]: string } = {
      'pending': 'Chờ xử lý',
      'approved': 'Đã chấp nhận',
      'rejected': 'Đã từ chối'
    }
    return statuses[status] || status
  }

  const hasAppeal = (reportId: number) => {
    if (!Array.isArray(appeals)) return false
    return appeals.some(appeal => appeal.report === reportId || appeal.report?.id === reportId)
  }

  const getAppealForReport = (reportId: number) => {
    if (!Array.isArray(appeals)) return undefined
    return appeals.find(appeal => appeal.report === reportId || appeal.report?.id === reportId)
  }

  if (!isAuthenticated) return null

  return (
    <main className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-100'}`}>
      <Header />
      
      {toast && (
        <ToastNotification
          message={toast.message}
          type={toast.type}
          isVisible={!!toast}
          onClose={() => setToast(null)}
        />
      )}

      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className={`text-3xl font-bold mb-6 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
          Báo Cáo & Kháng Cáo
        </h1>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold-400 mx-auto mb-4"></div>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Đang tải...</p>
          </div>
        ) : reports.length === 0 ? (
          <div className={`text-center py-12 rounded-lg ${theme === 'dark' ? 'bg-black/50' : 'bg-white'}`}>
            <i className={`pi pi-info-circle text-4xl mb-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}></i>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              Bạn chưa có báo cáo nào
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => {
              const appeal = getAppealForReport(report.id)
              const canAppeal = report.status === 'resolved' && !hasAppeal(report.id)
              
              return (
                <div
                  key={report.id}
                  className={`rounded-lg border-2 p-6 ${
                    theme === 'dark'
                      ? 'bg-black/50 border-gold-500/30'
                      : 'bg-white border-gold-400/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        {report.title}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          theme === 'dark' ? 'bg-gold-500/20 text-gold-400' : 'bg-gold-100 text-gold-700'
                        }`}>
                          {getReportTypeDisplay(report.report_type)}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                          report.status === 'resolved'
                            ? theme === 'dark' ? 'bg-red-500/20 text-red-400' : 'bg-red-100 text-red-700'
                            : theme === 'dark' ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {getStatusDisplay(report.status)}
                        </span>
                      </div>
                      <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className="font-semibold">Người báo cáo:</span> {report.reporter?.email || report.reporter?.username || 'N/A'}
                      </p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className="font-semibold">Ngày tạo:</span> {new Date(report.created_at).toLocaleDateString('vi-VN')}
                      </p>
                    </div>
                  </div>

                  <div className={`mb-4 p-4 rounded-lg ${theme === 'dark' ? 'bg-black/30' : 'bg-gray-50'}`}>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {report.description}
                    </p>
                    {report.evidence_image && (
                      <div className="mt-3">
                        <img
                          src={report.evidence_image.startsWith('http') 
                            ? report.evidence_image 
                            : `${getApiBaseUrlWithoutApi()}${report.evidence_image}`
                          }
                          alt="Bằng chứng"
                          className="max-w-xs rounded-lg"
                        />
                      </div>
                    )}
                  </div>

                  {report.admin_response && (
                    <div className={`mb-4 p-4 rounded-lg border-l-4 ${
                      theme === 'dark' 
                        ? 'bg-gold-500/10 border-gold-500' 
                        : 'bg-gold-50 border-gold-400'
                    }`}>
                      <p className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-gold-400' : 'text-gold-700'}`}>
                        Phản hồi của Admin:
                      </p>
                      <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {report.admin_response}
                      </p>
                    </div>
                  )}

                  {appeal && (
                    <div className={`mb-4 p-4 rounded-lg border-l-4 ${
                      appeal.status === 'approved'
                        ? theme === 'dark' ? 'bg-green-500/10 border-green-500' : 'bg-green-50 border-green-400'
                        : appeal.status === 'rejected'
                        ? theme === 'dark' ? 'bg-red-500/10 border-red-500' : 'bg-red-50 border-red-400'
                        : theme === 'dark' ? 'bg-yellow-500/10 border-yellow-500' : 'bg-yellow-50 border-yellow-400'
                    }`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className={`text-sm font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Kháng cáo: {getAppealStatusDisplay(appeal.status)}
                        </p>
                      </div>
                      <p className={`text-sm mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        {appeal.reason}
                      </p>
                      {appeal.admin_response && (
                        <div className="mt-2">
                          <p className={`text-sm font-semibold mb-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                            Phản hồi của Admin:
                          </p>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {appeal.admin_response}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {canAppeal && (
                    <button
                      onClick={() => {
                        setSelectedReport(report)
                        setShowAppealForm(true)
                      }}
                      className={`px-4 py-2 rounded-lg font-semibold transition ${
                        theme === 'dark'
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                      }`}
                    >
                      Kháng Cáo
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {/* Appeal Form Modal */}
        {showAppealForm && selectedReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
            <div className={`relative w-full max-w-2xl rounded-2xl border-2 ${
              theme === 'dark' 
                ? 'bg-black/95 border-gold-500/50' 
                : 'bg-white border-gold-400/50'
            } shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}>
              <button
                onClick={() => {
                  setShowAppealForm(false)
                  setSelectedReport(null)
                  setAppealReason('')
                  setAppealEvidence(null)
                }}
                className={`absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition ${
                  theme === 'dark'
                    ? 'bg-gold-500/20 hover:bg-gold-500/30 text-gold-400'
                    : 'bg-gold-400/20 hover:bg-gold-400/30 text-gold-600'
                }`}
              >
                <i className="pi pi-times text-sm"></i>
              </button>

              <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Kháng Cáo Báo Cáo
              </h2>

              <div className="space-y-4">
                <div>
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Lý do kháng cáo *
                  </label>
                  <textarea
                    value={appealReason}
                    onChange={(e) => setAppealReason(e.target.value)}
                    placeholder="Nhập lý do kháng cáo của bạn..."
                    rows={6}
                    required
                    className={`w-full px-4 py-2 rounded-lg border-2 ${
                      theme === 'dark' 
                        ? 'bg-black border-gold-500/30 text-white' 
                        : 'bg-white border-gold-400/30 text-gray-900'
                    } focus:outline-none focus:border-gold-400 resize-none`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    Hình ảnh bằng chứng (tùy chọn)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        setAppealEvidence(file)
                      }
                    }}
                    className={`w-full px-4 py-2 rounded-lg border-2 ${
                      theme === 'dark' 
                        ? 'bg-black border-gold-500/30 text-white' 
                        : 'bg-white border-gold-400/30 text-gray-900'
                    } focus:outline-none focus:border-gold-400`}
                  />
                  {appealEvidence && (
                    <p className={`text-xs mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      Đã chọn: {appealEvidence.name}
                    </p>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowAppealForm(false)
                      setSelectedReport(null)
                      setAppealReason('')
                      setAppealEvidence(null)
                    }}
                    className={`flex-1 py-2 px-4 rounded-lg transition ${
                      theme === 'dark' 
                        ? 'bg-gray-700 text-white hover:bg-gray-600' 
                        : 'bg-gray-200 text-gray-900 hover:bg-gray-300'
                    }`}
                  >
                    Hủy
                  </button>
                  <button
                    onClick={handleAppealSubmit}
                    disabled={submittingAppeal || !appealReason.trim()}
                    className="flex-1 py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {submittingAppeal ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Đang gửi...</span>
                      </>
                    ) : (
                      <>
                        <i className="pi pi-send text-sm"></i>
                        <span>Gửi Kháng Cáo</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

